import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts";
import axios from "axios";
import Select, { components } from "react-select";

import AnimatedCounter from "../../../components/AnimatedCounter/AnimatedCounter";
import ProgressRing from "../../../components/ProgressRing/ProgressRing";
import RotateDevice from "../../../components/RotateDevice/RotateDevice";

import "./DettaglioComm.css";
import Lf23Loader from "../../../components/Lf23Loader";

// Opzioni per i dropdown di configurazione tabella
// Nota: vista giornaliera rimossa per performance (aggregazione mensile lato DB)
const COLONNE_OPTIONS = [
    { value: 'anno', label: 'Anno', order: 1 },
    { value: 'trimestre', label: 'Trimestre', order: 2 },
    { value: 'mese', label: 'Mese', order: 3 }
];

const RIGHE_OPTIONS = [
    { value: 'settore', label: 'Settore', order: 1 },
    { value: 'commessa', label: 'Commessa', order: 2 },
    { value: 'attivitaLivello1', label: 'Attività Livello 1', order: 3 },
    { value: 'attivitaLivello2', label: 'Attività Livello 2', order: 4 },
    { value: 'dettaglioContratto', label: 'Dettaglio Contratto', order: 5 },
    { value: 'operatore', label: 'Operatore', order: 6 }
];

const INDICATORI_OPTIONS = [
    { value: 'oreLavorate', label: 'Ore Lavorate' },
    { value: 'oreNonLavorate', label: 'Ore Non Lavorate' },
    { value: 'oreNonLavoratePerc', label: 'Ore Non Lavorate %', isPerc: true },
    { value: 'oreDaContratto', label: 'Ore Da Contratto' },
    { value: 'oreDaTurno', label: 'Ore Da Turno' },
    { value: 'oreStraordinari', label: 'Ore Straordinari' },
    { value: 'oreStraordinariPerc', label: 'Ore Straordinari %', isPerc: true }
];

// Componente custom per mostrare "+N" quando ci sono molti elementi selezionati
const MultiValueContainer = ({ children, selectProps, data, ...props }) => {
    const values = selectProps.value || [];
    const index = values.findIndex(v => v.value === data.value);

    if (index < 2) {
        return <components.MultiValue selectProps={selectProps} data={data} {...props}>{children}</components.MultiValue>;
    }
    if (index === 2 && values.length > 2) {
        return (
            <div className="dettaglio-comm-select-more-badge">
                +{values.length - 2}
            </div>
        );
    }
    return null;
};

/**
 * Componente DettaglioComm - Gestisce la visualizzazione della tabella "Dettaglio Ore Per Attività"
 * con gerarchia espandibile a 5 livelli e del grafico "Top Commesse Per Straordinari"
 *
 * Livelli gerarchia:
 * 1. Settore
 * 2. Commessa
 * 3. Dettaglio Contratto
 * 4. Giustificativo
 * 5. Operatore
 *
 * Props:
 * - unfilteredHrData: array di dati grezzi dal backend
 * - loading: boolean per indicare lo stato di caricamento
 * - fromDate: string data inizio (formato yyyy-mm-dd)
 * - toDate: string data fine (formato yyyy-mm-dd)
 * - selectedCategory: string categoria selezionata
 * - filterOptions: oggetto con le opzioni dei filtri (include commesse con id e label)
 */
export const DettaglioComm = ({
    unfilteredHrData = [],
    loading = false,
    fromDate,
    toDate,
    selectedCategory = "",
    filterOptions = {}
}) => {
    // Stati per i dropdown di configurazione tabella
    const [selectedColonne, setSelectedColonne] = useState([
        { value: 'mese', label: 'Mese', order: 3 }
    ]);
    const [selectedRighe, setSelectedRighe] = useState([
        { value: 'settore', label: 'Settore', order: 1 },
        { value: 'commessa', label: 'Commessa', order: 2 },
        { value: 'dettaglioContratto', label: 'Dettaglio Contratto', order: 5 },
        { value: 'operatore', label: 'Operatore', order: 6 }
    ]);
    const [selectedIndicatori, setSelectedIndicatori] = useState(INDICATORI_OPTIONS);

    const [selectedCommesse, setSelectedCommesse] = useState([]); // Commesse selezionate dal grafico
    const [expandedRows, setExpandedRows] = useState({}); // Traccia righe espanse: { "settore1": true, "settore1|commessa1": true, ... }
    const [isFullscreenTable, setIsFullscreenTable] = useState(false);
    const [isFullscreenChart, setIsFullscreenChart] = useState(false);
    const chartRef = useRef(null);
    const chartSectionRef = useRef(null); // Ref per la sezione grafico
    const tableSectionRef = useRef(null); // Ref per la sezione tabella
    const modifierKeyRef = useRef(false); // Traccia se Ctrl/Cmd è premuto
    const clickProcessingRef = useRef(false); // Previene click multipli
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    // Reset della selezione quando cambiano i dati dal backend
    useEffect(() => {
        setSelectedCommesse([]);
    }, [unfilteredHrData]);

    // Chiudi fullscreen con Escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setIsFullscreenTable(false);
                setIsFullscreenChart(false);
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    // Sincronizza l'altezza della tabella con quella del grafico
    useEffect(() => {
        let rafId = null;

        const syncHeight = () => {
            if (chartSectionRef.current && tableSectionRef.current) {
                const chartHeight = chartSectionRef.current.offsetHeight;
                tableSectionRef.current.style.maxHeight = `${chartHeight}px`;
            }
        };

        // Esegui subito
        syncHeight();

        // Usa ResizeObserver per rilevare cambiamenti di dimensione del grafico
        // Wrappa in requestAnimationFrame per evitare l'errore "loop completed"
        const resizeObserver = new ResizeObserver(() => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
                syncHeight();
            });
        });

        if (chartSectionRef.current) {
            resizeObserver.observe(chartSectionRef.current);
        }

        return () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            resizeObserver.disconnect();
        };
    }, [selectedCommesse, unfilteredHrData]);

    // Listener per tracciare Ctrl/Cmd key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.metaKey || e.ctrlKey) {
                modifierKeyRef.current = true;
            }
        };
        const handleKeyUp = (e) => {
            if (!e.metaKey && !e.ctrlKey) {
                modifierKeyRef.current = false;
            }
        };
        const handleBlur = () => {
            modifierKeyRef.current = false;
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', handleBlur);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', handleBlur);
        };
    }, []);

    /* Funzione per effettuare il download del file tabella */
    const handleDownloadFile = async (data) => {
        try {
            const response = await axios.post(`${API_URL}/operation-HR/downloadExcel`,
                { tableName: selectedCategory, datiDaStampare: data },
                { responseType: "blob" }
            );

            if (response.data.type === "application/json") {
                const text = await response.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.error || "Errore durante il download");
            }

            const contentDisposition = response.headers['content-disposition'];
            let fileName = `${selectedCategory}.xlsx`;

            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (fileNameMatch && fileNameMatch[1]) {
                    fileName = fileNameMatch[1];
                }
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Errore durante il download del file:", error);
        }
    };

    /* Funzione per effettuare il download del grafico commesse */
    const handleDownloadChartCommesse = async () => {
        try {
            const dataToExport = {
                headers: ['Commessa', 'Ore Straordinarie'],
                rows: chartData.commesse.map((c, idx) => [c, chartData.straordinari[idx]])
            };

            const response = await axios.post(`${API_URL}/operation-HR/downloadExcel`,
                { tableName: 'Top Commesse Straordinari', datiDaStampare: dataToExport },
                { responseType: "blob" }
            );

            if (response.data.type === "application/json") {
                const text = await response.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.error || "Errore durante il download");
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "top_commesse_straordinari.xlsx");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Errore durante il download del file:", error);
        }
    };

    /**
     * Crea una mappa id -> label per le commesse
     */
    const commesseIdToLabel = useMemo(() => {
        const map = new Map();
        if (filterOptions.commesse) {
            filterOptions.commesse.forEach(c => {
                map.set(c.value, c.label);
            });
        }
        return map;
    }, [filterOptions.commesse]);

    /**
     * Crea una mappa label -> id per le commesse (per il click sul grafico)
     */
    const commesseLabelToId = useMemo(() => {
        const map = new Map();
        if (filterOptions.commesse) {
            filterOptions.commesse.forEach(c => {
                map.set(c.label, c.value);
            });
        }
        return map;
    }, [filterOptions.commesse]);

    /**
     * Crea una mappa id -> label per settori
     */
    const settoriIdToLabel = useMemo(() => {
        const map = new Map();
        if (filterOptions.settori) {
            filterOptions.settori.forEach(s => {
                map.set(s.value, s.label);
            });
        }
        return map;
    }, [filterOptions.settori]);

    /**
     * Crea una mappa id -> label per dettaglio contratto
     */
    const dettaglioContrattoIdToLabel = useMemo(() => {
        const map = new Map();
        if (filterOptions.dettagliContratto) {
            filterOptions.dettagliContratto.forEach(d => {
                map.set(d.value, d.label);
            });
        }
        return map;
    }, [filterOptions.dettagliContratto]);

    /**
     * Crea una mappa id -> label per operatori
     */
    const operatoriIdToLabel = useMemo(() => {
        const map = new Map();
        if (filterOptions.operatori) {
            filterOptions.operatori.forEach(o => {
                map.set(o.value, o.label);
            });
        }
        return map;
    }, [filterOptions.operatori]);

    /**
     * Filtra i dati in base alle commesse selezionate dal grafico
     */
    const filteredHrData = useMemo(() => {
        // Assicurati che unfilteredHrData sia un array
        const dataArray = Array.isArray(unfilteredHrData) ? unfilteredHrData : [];

        if (selectedCommesse.length === 0) {
            return dataArray;
        }
        return dataArray.filter(item => {
            const commessaId = item.commessa || "N/D";
            return selectedCommesse.includes(commessaId);
        });
    }, [unfilteredHrData, selectedCommesse]);

    /**
     * Mappa per estrarre il valore di una dimensione riga da un record
     */
    const getDimensionValue = useCallback((d, dimension) => {
        switch (dimension) {
            case 'settore': return d.settore || "N/D";
            case 'commessa': return d.commessa || "N/D";
            case 'attivitaLivello1': return d.attivitaLivello1 || "N/D";
            case 'attivitaLivello2': return d.attivitaLivello2 || "N/D";
            case 'dettaglioContratto': return d.dettaglioContratto || "N/D";
            case 'operatore': return d.operatore || "N/D";
            default: return "N/D";
        }
    }, []);

    /**
     * Mappa per ottenere la label di una dimensione riga
     */
    const getDimensionLabel = useCallback((dimension, value) => {
        switch (dimension) {
            case 'settore': return settoriIdToLabel.get(value) || value;
            case 'commessa': return commesseIdToLabel.get(value) || value;
            case 'dettaglioContratto': return dettaglioContrattoIdToLabel.get(value) || value;
            case 'operatore': {
                const numericId = String(value).replace('Operatore ', '');
                return operatoriIdToLabel.get(numericId) || value;
            }
            default: return value;
        }
    }, [settoriIdToLabel, commesseIdToLabel, dettaglioContrattoIdToLabel, operatoriIdToLabel]);

    /**
     * Calcola i dati della tabella gerarchica con livelli dinamici
     * Livelli determinati da selectedRighe
     * Colonne determinate da selectedColonne (con annidamento gerarchico)
     */
    const tableData = useMemo(() => {
        if (filteredHrData.length === 0) return { hierarchy: {}, colonneStruttura: [], totaleGenerale: {}, flatPeriodi: [] };

        const mesiMap = {
            'Gennaio': 1, 'Febbraio': 2, 'Marzo': 3, 'Aprile': 4,
            'Maggio': 5, 'Giugno': 6, 'Luglio': 7, 'Agosto': 8,
            'Settembre': 9, 'Ottobre': 10, 'Novembre': 11, 'Dicembre': 12
        };

        const mesiAbbreviati = {
            'Gennaio': 'Gen', 'Febbraio': 'Feb', 'Marzo': 'Mar', 'Aprile': 'Apr',
            'Maggio': 'Mag', 'Giugno': 'Giu', 'Luglio': 'Lug', 'Agosto': 'Ago',
            'Settembre': 'Set', 'Ottobre': 'Ott', 'Novembre': 'Nov', 'Dicembre': 'Dic'
        };

        // Ordina le colonne selezionate per order (anno < trimestre < mese < giorno)
        const sortedColonne = [...selectedColonne].sort((a, b) => a.order - b.order);

        // Ordina le righe selezionate per order
        const sortedRighe = [...selectedRighe].sort((a, b) => a.order - b.order);

        // Estrae i periodi unici per ogni granularità selezionata
        const extractPeriodi = (granularita) => {
            switch (granularita) {
                case 'anno':
                    return [...new Set(filteredHrData.map(d => d.anno))].sort().map(a => ({
                        type: 'anno',
                        value: a,
                        label: String(a),
                        key: `anno-${a}`
                    }));
                case 'trimestre': {
                    // Calcola i trimestri dai mesi disponibili
                    const trimAnnoSet = new Set();
                    filteredHrData.forEach(d => {
                        if (d.mese && d.anno) {
                            const numeroMese = mesiMap[d.mese];
                            if (numeroMese) {
                                const trimNum = Math.ceil(numeroMese / 3);
                                trimAnnoSet.add(`Q${trimNum}-${d.anno}`);
                            }
                        }
                    });
                    return Array.from(trimAnnoSet).map(trimAnno => {
                        const [trim, anno] = trimAnno.split('-');
                        const trimNum = parseInt(trim.replace('Q', ''));
                        return {
                            type: 'trimestre',
                            value: trim,
                            anno: parseInt(anno),
                            trimestre: trimNum,
                            label: `${trim} ${anno}`,
                            key: `trim-${trim}-${anno}`
                        };
                    }).sort((a, b) => {
                        if (a.anno !== b.anno) return a.anno - b.anno;
                        return a.trimestre - b.trimestre;
                    });
                }
                case 'mese': {
                    const mesiAnnoSet = new Set();
                    filteredHrData.forEach(d => {
                        if (d.mese && d.anno) mesiAnnoSet.add(`${d.mese}-${d.anno}`);
                    });
                    return Array.from(mesiAnnoSet).map(meseAnno => {
                        const [mese, anno] = meseAnno.split('-');
                        return {
                            type: 'mese',
                            mese,
                            anno: parseInt(anno),
                            numeroMese: mesiMap[mese],
                            label: `${mesiAbbreviati[mese]} ${anno}`,
                            key: meseAnno
                        };
                    }).sort((a, b) => {
                        if (a.anno !== b.anno) return a.anno - b.anno;
                        return a.numeroMese - b.numeroMese;
                    });
                }
                case 'giorno': {
                    const giorniSet = new Set();
                    filteredHrData.forEach(d => {
                        if (d.giorno && d.mese && d.anno) {
                            giorniSet.add(`${d.giorno}-${d.mese}-${d.anno}`);
                        }
                    });
                    return Array.from(giorniSet).map(giornoStr => {
                        const [giorno, mese, anno] = giornoStr.split('-');
                        return {
                            type: 'giorno',
                            giorno: parseInt(giorno),
                            mese,
                            anno: parseInt(anno),
                            numeroMese: mesiMap[mese],
                            label: `${giorno} ${mesiAbbreviati[mese]}`,
                            key: giornoStr
                        };
                    }).sort((a, b) => {
                        if (a.anno !== b.anno) return a.anno - b.anno;
                        if (a.numeroMese !== b.numeroMese) return a.numeroMese - b.numeroMese;
                        return a.giorno - b.giorno;
                    });
                }
                default:
                    return [];
            }
        };

        // Costruisce la struttura annidata delle colonne
        // Es: se seleziono Anno + Mese, ottengo { 2024: [Gen24, Feb24...], 2025: [Gen25...] }
        const buildColonneStruttura = () => {
            if (sortedColonne.length === 0) return [];

            // Se c'è una sola granularità, ritorna lista flat
            if (sortedColonne.length === 1) {
                const periodi = extractPeriodi(sortedColonne[0].value);
                return periodi.map(p => ({
                    ...p,
                    children: null
                }));
            }

            // Costruisce struttura annidata
            const topLevel = extractPeriodi(sortedColonne[0].value);

            const buildChildren = (parentPeriodo, levelIndex) => {
                if (levelIndex >= sortedColonne.length) return null;

                const granularita = sortedColonne[levelIndex].value;
                let childPeriodi = extractPeriodi(granularita);

                // Filtra i periodi figli per appartenenza al parent
                childPeriodi = childPeriodi.filter(child => {
                    // Funzione helper per ottenere il trimestre da un numero mese
                    const getTrimestreFromMese = (numeroMese) => Math.ceil(numeroMese / 3);

                    if (parentPeriodo.type === 'anno') {
                        // Anno contiene trimestri, mesi o giorni dello stesso anno
                        return child.anno === parentPeriodo.value;
                    } else if (parentPeriodo.type === 'trimestre') {
                        // Il trimestre contiene i mesi/giorni corrispondenti
                        const trimNum = parentPeriodo.trimestre;
                        if (child.type === 'mese') {
                            return getTrimestreFromMese(child.numeroMese) === trimNum && child.anno === parentPeriodo.anno;
                        } else if (child.type === 'giorno') {
                            return getTrimestreFromMese(child.numeroMese) === trimNum && child.anno === parentPeriodo.anno;
                        } else if (child.type === 'trimestre') {
                            return child.trimestre === trimNum && child.anno === parentPeriodo.anno;
                        }
                        return child.anno === parentPeriodo.anno;
                    } else if (parentPeriodo.type === 'mese') {
                        // Il mese contiene solo i giorni dello stesso mese e anno
                        if (child.type === 'giorno') {
                            return child.mese === parentPeriodo.mese && child.anno === parentPeriodo.anno;
                        }
                        return child.mese === parentPeriodo.mese && child.anno === parentPeriodo.anno;
                    } else if (parentPeriodo.type === 'giorno') {
                        // Il giorno non ha figli temporali
                        return false;
                    }
                    return true;
                });

                // Se non ci sono figli dopo il filtro, ritorna null
                if (childPeriodi.length === 0) return null;

                return childPeriodi.map(p => ({
                    ...p,
                    children: buildChildren(p, levelIndex + 1)
                }));
            };

            return topLevel.map(p => ({
                ...p,
                children: buildChildren(p, 1)
            }));
        };

        const colonneStruttura = buildColonneStruttura();

        // Crea lista flat di tutti i periodi foglia (per il calcolo delle metriche)
        const getFlatPeriodi = (struttura) => {
            const flat = [];
            const traverse = (nodes) => {
                nodes.forEach(node => {
                    if (node.children && node.children.length > 0) {
                        traverse(node.children);
                    } else {
                        flat.push(node);
                    }
                });
            };
            traverse(struttura);
            return flat;
        };

        const flatPeriodi = getFlatPeriodi(colonneStruttura);

        // Funzione per verificare se un record appartiene a un periodo
        const matchPeriodo = (d, periodo) => {
            switch (periodo.type) {
                case 'anno':
                    return d.anno === periodo.value;
                case 'trimestre': {
                    // Calcola il trimestre dal mese del record
                    const numeroMese = mesiMap[d.mese];
                    const recordTrim = numeroMese ? Math.ceil(numeroMese / 3) : 0;
                    return recordTrim === periodo.trimestre && d.anno === periodo.anno;
                }
                case 'mese':
                    return d.mese === periodo.mese && d.anno === periodo.anno;
                case 'giorno':
                    return d.giorno === periodo.giorno && d.mese === periodo.mese && d.anno === periodo.anno;
                default:
                    return false;
            }
        };

        // Funzione per calcolare le metriche da un array di dati
        const calcMetriche = (dataArray) => {
            const oreLavorate = dataArray.reduce((sum, d) => sum + (d.oreLavorate || 0), 0);
            const oreNonLavorate = dataArray.reduce((sum, d) => sum + (d.oreNonLavorate || 0), 0);
            const oreDaContratto = dataArray.reduce((sum, d) => sum + (d.oreDaContratto || 0), 0);
            const oreDaTurno = dataArray.reduce((sum, d) => sum + (d.oreDaTurno || 0), 0);
            const oreStraordinari = oreLavorate + oreNonLavorate - oreDaContratto;

            return {
                oreLavorate: Number(oreLavorate.toFixed(1)),
                oreNonLavorate: Number(oreNonLavorate.toFixed(1)),
                oreNonLavoratePerc: oreDaContratto > 0 ? Number(((oreNonLavorate / oreDaContratto) * 100).toFixed(1)) : 0,
                oreDaContratto: Number(oreDaContratto.toFixed(1)),
                oreDaTurno: Number(oreDaTurno.toFixed(1)),
                oreStraordinari: Number(oreStraordinari.toFixed(1)),
                oreStraordinariPerc: oreDaContratto > 0 ? Number(((oreStraordinari / oreDaContratto) * 100).toFixed(1)) : 0
            };
        };

        // Costruisce la gerarchia dinamica basata su selectedRighe
        const hierarchy = {};

        filteredHrData.forEach(d => {
            if (sortedRighe.length === 0) return;

            let currentLevel = hierarchy;

            sortedRighe.forEach((rigaDim, index) => {
                const dimValue = getDimensionValue(d, rigaDim.value);
                const isLast = index === sortedRighe.length - 1;

                if (!currentLevel[dimValue]) {
                    currentLevel[dimValue] = {
                        dimension: rigaDim.value,
                        children: isLast ? null : {},
                        data: []
                    };
                }
                currentLevel[dimValue].data.push(d);

                if (!isLast) {
                    currentLevel = currentLevel[dimValue].children;
                }
            });
        });

        // Calcola le metriche per ogni nodo della gerarchia
        const processNode = (node) => {
            // Calcola metriche per ogni periodo foglia
            node.metriche = {};
            flatPeriodi.forEach((periodo) => {
                const filtered = node.data.filter(d => matchPeriodo(d, periodo));
                node.metriche[periodo.key] = calcMetriche(filtered);
            });

            // Calcola totale
            node.totale = calcMetriche(node.data);

            // Processa i figli se esistono
            if (node.children) {
                Object.values(node.children).forEach(child => processNode(child));
            }
        };

        Object.values(hierarchy).forEach(node => processNode(node));

        // Calcola totale generale
        const totaleGenerale = {
            metriche: {},
            totale: calcMetriche(filteredHrData)
        };

        flatPeriodi.forEach((periodo) => {
            const filtered = filteredHrData.filter(d => matchPeriodo(d, periodo));
            totaleGenerale.metriche[periodo.key] = calcMetriche(filtered);
        });

        return {
            hierarchy,
            colonneStruttura,
            flatPeriodi,
            totaleGenerale,
            sortedRighe
        };
    }, [filteredHrData, selectedColonne, selectedRighe, getDimensionValue]);

    /**
     * Toggle per espandere/collassare una riga
     */
    const toggleRow = useCallback((rowKey) => {
        setExpandedRows(prev => ({
            ...prev,
            [rowKey]: !prev[rowKey]
        }));
    }, []);

    /**
     * Verifica se una riga è espansa
     */
    const isExpanded = useCallback((rowKey) => {
        return expandedRows[rowKey] === true;
    }, [expandedRows]);

    /**
     * Calcola i dati per il grafico "Top Commesse Per Straordinari"
     */
    const chartData = useMemo(() => {
        // Assicurati che unfilteredHrData sia un array
        const dataArray = Array.isArray(unfilteredHrData) ? unfilteredHrData : [];
        if (dataArray.length === 0) return { commesse: [], straordinari: [], commesseIds: [] };

        // Raggruppa per commessa (usando l'id)
        const commesseMap = new Map();

        dataArray.forEach(item => {
            const commessaId = item.commessa || "N/D";
            if (!commesseMap.has(commessaId)) {
                commesseMap.set(commessaId, {
                    commessaId,
                    oreLavorate: 0,
                    oreNonLavorate: 0,
                    oreDaContratto: 0
                });
            }
            const data = commesseMap.get(commessaId);
            data.oreLavorate += item.oreLavorate || 0;
            data.oreNonLavorate += item.oreNonLavorate || 0;
            data.oreDaContratto += item.oreDaContratto || 0;
        });

        // Calcola ore straordinarie e ordina
        const commesseArray = Array.from(commesseMap.values())
            .map(c => {
                // Converti l'id in label usando la mappa
                const commessaLabel = commesseIdToLabel.get(c.commessaId) || c.commessaId || "N/D";
                return {
                    commessaId: c.commessaId,
                    commessa: commessaLabel,
                    oreStraordinari: Number((c.oreLavorate + c.oreNonLavorate - c.oreDaContratto).toFixed(1))
                };
            })
            .filter(c => c.oreStraordinari > 0)
            .sort((a, b) => b.oreStraordinari - a.oreStraordinari)
            .slice(0, 15); // Top 15

        return {
            commesse: commesseArray.map(c => c.commessa),
            commesseIds: commesseArray.map(c => c.commessaId),
            straordinari: commesseArray.map(c => c.oreStraordinari)
        };
    }, [unfilteredHrData, commesseIdToLabel]);

    /**
     * Handler per il click sulle barre del grafico
     * Click singolo: seleziona solo quella commessa
     * Cmd/Ctrl + Click: aggiunge/rimuove dalla selezione multipla
     */
    const handleChartClick = (params) => {
        if (!params || params.componentType !== 'series') return;

        // Previene click multipli dovuti a re-render
        if (clickProcessingRef.current) return;
        clickProcessingRef.current = true;
        setTimeout(() => { clickProcessingRef.current = false; }, 100);

        const clickedIndex = params.dataIndex;
        // I dati nel grafico sono reversed, quindi l'indice ECharts corrisponde ai dati reversed
        const reversedIds = chartData.commesseIds.slice().reverse();
        const commessaId = reversedIds[clickedIndex];

        if (!commessaId) return;

        // Verifica se Ctrl/Cmd è premuto usando il ref
        const isMultiSelect = modifierKeyRef.current;

        setSelectedCommesse(prev => {
            if (isMultiSelect) {
                // Multi-selezione: toggle della commessa
                if (prev.includes(commessaId)) {
                    // Rimuovi dalla selezione
                    return prev.filter(id => id !== commessaId);
                } else {
                    // Aggiungi alla selezione
                    return [...prev, commessaId];
                }
            } else {
                // Click singolo: se è già l'unica selezionata, deseleziona tutto
                if (prev.length === 1 && prev[0] === commessaId) {
                    return [];
                }
                // Altrimenti seleziona solo questa
                return [commessaId];
            }
        });
    };

    /**
     * Resetta la selezione delle commesse
     */
    const handleClearSelection = useCallback(() => {
        setSelectedCommesse([]);
    }, []);

    /**
     * Formatta i numeri con separatore migliaia (.) e decimali (,)
     * Massimo 2 cifre decimali con arrotondamento
     */
    const formatNumber = (num) => {
        return new Intl.NumberFormat('it-IT', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
            useGrouping: true
        }).format(num);
    };

    /**
     * Configurazione del grafico a barre orizzontali
     */
    const getChartOptions = useCallback(() => {
        const { commesse, commesseIds, straordinari } = chartData;

        // Prepara i dati con colori diversi per le barre selezionate
        const reversedCommesse = commesse.slice().reverse();
        const reversedIds = commesseIds.slice().reverse();
        const reversedStraordinari = straordinari.slice().reverse();

        // Colori per barre selezionate e non selezionate - rosso soft coerente con ore straordinarie
        const selectedColor = new echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: 'rgba(185, 28, 28, 0.85)' },
            { offset: 0.5, color: 'rgba(220, 38, 38, 0.9)' },
            { offset: 1, color: 'rgba(153, 27, 27, 1)' }
        ]);

        const normalColor = new echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: 'rgba(185, 28, 28, 0.6)' },
            { offset: 0.5, color: 'rgba(185, 28, 28, 0.75)' },
            { offset: 1, color: 'rgba(153, 27, 27, 0.85)' }
        ]);

        const dimmedColor = new echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: 'rgba(185, 28, 28, 0.25)' },
            { offset: 0.5, color: 'rgba(185, 28, 28, 0.35)' },
            { offset: 1, color: 'rgba(153, 27, 27, 0.45)' }
        ]);

        // Crea array di dati con stili personalizzati per ogni barra
        const dataWithStyles = reversedStraordinari.map((value, index) => {
            const commessaId = reversedIds[index];
            const isSelected = selectedCommesse.includes(commessaId);
            const hasSelection = selectedCommesse.length > 0;

            let itemColor;
            let labelColor;

            if (hasSelection) {
                if (isSelected) {
                    itemColor = selectedColor;
                    labelColor = '#991b1b';
                } else {
                    itemColor = dimmedColor;
                    labelColor = '#9ca3af';
                }
            } else {
                itemColor = normalColor;
                labelColor = '#b91c1c';
            }

            return {
                value,
                itemStyle: {
                    color: itemColor,
                    borderRadius: [0, 4, 4, 0]
                },
                label: {
                    show: true,
                    position: 'right',
                    formatter: (params) => `${formatNumber(params.value)} h`,
                    color: labelColor,
                    fontWeight: 'bold',
                    fontSize: 11
                }
            };
        });

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                },
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderColor: '#e2e8f0',
                borderWidth: 1,
                padding: [12, 16],
                textStyle: {
                    color: '#334155',
                    fontSize: 12
                },
                extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); border-radius: 8px;',
                formatter: function (params) {
                    let result = `<div style="font-weight: 600; margin-bottom: 8px; font-size: 13px; color: #1e293b;">${params[0].name}</div>`;
                    result += `<div style="display: flex; justify-content: space-between; margin: 4px 0;">`;
                    result += `${params[0].marker} <span style="flex: 1; margin-left: 8px;">Ore Straordinari</span>`;
                    result += `<span style="font-weight: 600; margin-left: 16px; color: #dc2626;">${formatNumber(params[0].value)} h</span></div>`;
                    if (selectedCommesse.length === 0) {
                        result += '<div style="margin-top: 8px; font-size: 10px; color: #94a3b8;">Click per filtrare • Cmd/Ctrl+Click per multi-selezione</div>';
                    }
                    return result;
                }
            },
            grid: {
                left: '3%',
                right: '6%',
                bottom: '3%',
                top: '10',
                containLabel: true
            },
            xAxis: {
                type: 'value',
                name: 'Ore',
                axisLabel: {
                    fontSize: 11,
                    formatter: (value) => formatNumber(value)
                }
            },
            yAxis: {
                type: 'category',
                data: reversedCommesse,
                axisLabel: {
                    fontSize: 10,
                    width: 100,
                    overflow: 'truncate',
                    ellipsis: '...'
                }
            },
            series: [
                {
                    name: 'Ore Straordinari',
                    type: 'bar',
                    data: dataWithStyles,
                    emphasis: {
                        itemStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                                { offset: 0, color: 'rgba(220, 38, 38, 0.9)' },
                                { offset: 0.5, color: 'rgba(185, 28, 28, 0.95)' },
                                { offset: 1, color: 'rgba(153, 27, 27, 1)' }
                            ])
                        }
                    },
                    cursor: 'pointer'
                }
            ],
            animationDuration: 800,
            animationEasing: 'cubicOut',
            animationDelay: function (idx) {
                return idx * 30;
            }
        };
    }, [chartData, selectedCommesse, commesseLabelToId]);

    /**
     * Ritorna la classe CSS per la cella in base al valore e al tipo
     */
    const getCellClass = (type, valore) => {
        if (type === 'straordinari') {
            // Ore straordinarie sempre in rosso soft
            return 'value-straordinari';
        }
        if (type === 'percNonLav') {
            if (valore > 15) return 'value-danger';
            if (valore > 10) return 'value-high';
        }
        return '';
    };

    // Verifica se i dati sono nel formato corretto per questa tab (deve essere un array, non oggetto)
    const hasValidDataFormat = Array.isArray(unfilteredHrData);

    // Mostra il loader se sta caricando O se i dati non sono nel formato corretto (es. oggetto da altra tab)
    const dataArrayForCheck = hasValidDataFormat ? unfilteredHrData : [];
    const isLoading = loading || !hasValidDataFormat;
    const isDataReady = !loading && hasValidDataFormat && dataArrayForCheck.length > 0;
    const isEmpty = !loading && hasValidDataFormat && dataArrayForCheck.length === 0;

    /**
     * Calcola i KPI riassuntivi per le summary cards
     */
    const kpiSummary = useMemo(() => {
        if (filteredHrData.length === 0) {
            return {
                numOperatori: 0,
                numCommesse: 0,
                oreLavorate: 0,
                oreNonLavorate: 0,
                oreDaContratto: 0,
                oreStraordinarie: 0,
                percAssenteismo: 0,
                mediaOreOperatore: 0
            };
        }

        // Operatori unici
        const operatoriSet = new Set(filteredHrData.map(d => d.operatore).filter(Boolean));
        const numOperatori = operatoriSet.size;

        // Commesse uniche
        const commesseSet = new Set(filteredHrData.map(d => d.commessa).filter(Boolean));
        const numCommesse = commesseSet.size;

        // Totali ore
        const oreLavorate = filteredHrData.reduce((sum, d) => sum + (d.oreLavorate || 0), 0);
        const oreNonLavorate = filteredHrData.reduce((sum, d) => sum + (d.oreNonLavorate || 0), 0);
        const oreDaContratto = filteredHrData.reduce((sum, d) => sum + (d.oreDaContratto || 0), 0);
        const oreStraordinarie = oreLavorate + oreNonLavorate - oreDaContratto;

        // Percentuale assenteismo
        const percAssenteismo = oreDaContratto > 0 ? (oreNonLavorate / oreDaContratto) * 100 : 0;

        // Media ore per operatore
        const mediaOreOperatore = numOperatori > 0 ? oreLavorate / numOperatori : 0;

        return {
            numOperatori,
            numCommesse,
            oreLavorate: Number(oreLavorate.toFixed(1)),
            oreNonLavorate: Number(oreNonLavorate.toFixed(1)),
            oreDaContratto: Number(oreDaContratto.toFixed(1)),
            oreStraordinarie: Number(oreStraordinarie.toFixed(1)),
            percAssenteismo: Number(percAssenteismo.toFixed(1)),
            mediaOreOperatore: Number(mediaOreOperatore.toFixed(1))
        };
    }, [filteredHrData]);

    // Metriche filtrate in base alla selezione
    const metriche = useMemo(() => {
        return selectedIndicatori.map(ind => ({
            key: ind.value,
            label: ind.label.replace('Ore ', '').replace(' %', '%'),
            isPerc: ind.isPerc || false
        }));
    }, [selectedIndicatori]);

    /**
     * Prepara i dati per l'export Excel
     * Appiattisce la gerarchia al livello più basso e crea righe con tutte le dimensioni
     */
    const prepareExportData = useCallback(() => {
        const { hierarchy, flatPeriodi, sortedRighe } = tableData;
        if (!hierarchy || Object.keys(hierarchy).length === 0) return null;

        // Costruisce gli header
        // Prima le dimensioni riga
        const dimensionHeaders = sortedRighe.map(r => r.label);

        // Poi periodi × indicatori
        const periodoHeaders = [];
        flatPeriodi.forEach(periodo => {
            metriche.forEach(m => {
                periodoHeaders.push(`${periodo.label} - ${m.label}`);
            });
        });

        // Poi totali × indicatori
        const totaleHeaders = metriche.map(m => `Totale - ${m.label}`);

        const headers = [...dimensionHeaders, ...periodoHeaders, ...totaleHeaders];

        // Funzione ricorsiva per raccogliere tutte le righe foglia
        const rows = [];

        const collectLeafRows = (node, nodeId, parentPath, level) => {
            const currentPath = [...parentPath, nodeId];
            const hasChildren = node.children && Object.keys(node.children).length > 0;

            if (!hasChildren) {
                // Nodo foglia: crea la riga
                const row = [];

                // Aggiungi le dimensioni (con label corrette)
                currentPath.forEach((pathValue, idx) => {
                    const dimension = sortedRighe[idx]?.value;
                    const label = getDimensionLabel(dimension, pathValue);
                    row.push(label);
                });

                // Aggiungi metriche per ogni periodo
                flatPeriodi.forEach(periodo => {
                    const periodoMetriche = node.metriche?.[periodo.key] || {};
                    metriche.forEach(m => {
                        const val = periodoMetriche[m.key] || 0;
                        row.push(m.isPerc ? `${val}%` : val);
                    });
                });

                // Aggiungi totali
                metriche.forEach(m => {
                    const val = node.totale?.[m.key] || 0;
                    row.push(m.isPerc ? `${val}%` : val);
                });

                rows.push(row);
            } else {
                // Nodo con figli: scendi ricorsivamente
                Object.entries(node.children)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .forEach(([childId, childNode]) => {
                        collectLeafRows(childNode, childId, currentPath, level + 1);
                    });
            }
        };

        // Inizia dalla radice
        Object.entries(hierarchy)
            .sort(([a], [b]) => a.localeCompare(b))
            .forEach(([nodeId, node]) => {
                collectLeafRows(node, nodeId, [], 0);
            });

        // Aggiungi riga totale generale
        const totaleRow = [];
        // Dimensioni vuote tranne la prima che dice "TOTALE"
        sortedRighe.forEach((_, idx) => {
            totaleRow.push(idx === 0 ? 'TOTALE' : '');
        });
        // Metriche per ogni periodo
        flatPeriodi.forEach(periodo => {
            const periodoMetriche = tableData.totaleGenerale?.metriche?.[periodo.key] || {};
            metriche.forEach(m => {
                const val = periodoMetriche[m.key] || 0;
                totaleRow.push(m.isPerc ? `${val}%` : val);
            });
        });
        // Totali generali
        metriche.forEach(m => {
            const val = tableData.totaleGenerale?.totale?.[m.key] || 0;
            totaleRow.push(m.isPerc ? `${val}%` : val);
        });
        rows.push(totaleRow);

        return { headers, rows };
    }, [tableData, metriche, getDimensionLabel]);

    /**
     * Calcola la profondità massima della struttura colonne (per rowSpan header)
     */
    const getMaxDepth = useCallback((struttura) => {
        let maxDepth = 1;
        const traverse = (nodes, depth) => {
            nodes.forEach(node => {
                if (node.children && node.children.length > 0) {
                    traverse(node.children, depth + 1);
                } else {
                    maxDepth = Math.max(maxDepth, depth);
                }
            });
        };
        traverse(struttura, 1);
        return maxDepth;
    }, []);

    /**
     * Calcola il numero di foglie sotto un nodo (per colSpan)
     */
    const getLeafCount = useCallback((node) => {
        if (!node.children || node.children.length === 0) return 1;
        return node.children.reduce((sum, child) => sum + getLeafCount(child), 0);
    }, []);

    /**
     * Genera le righe dell'header per colonne annidate
     */
    const renderHeaderRows = useCallback(() => {
        const { colonneStruttura, flatPeriodi } = tableData;
        if (!colonneStruttura || colonneStruttura.length === 0) return null;

        const maxDepth = getMaxDepth(colonneStruttura);
        const headerRows = [];

        // Per ogni livello di profondità, genera una riga
        for (let level = 0; level < maxDepth; level++) {
            const cells = [];

            // Prima colonna: label delle righe
            if (level === 0) {
                cells.push(
                    <th key="righe-label" rowSpan={maxDepth + 1} className="th-settore">
                        {selectedRighe.length > 0 ? selectedRighe[0].label : 'Righe'}
                    </th>
                );
            }

            // Colonne periodo
            const addCellsForLevel = (nodes, currentLevel) => {
                nodes.forEach((node, idx) => {
                    if (currentLevel === level) {
                        // Questo nodo va in questa riga
                        const leafCount = getLeafCount(node);
                        const colSpan = leafCount * metriche.length;
                        const hasChildren = node.children && node.children.length > 0;
                        const rowSpan = hasChildren ? 1 : (maxDepth - level);

                        cells.push(
                            <th
                                key={`${node.key}-${level}`}
                                colSpan={colSpan}
                                rowSpan={rowSpan}
                                className="th-periodo"
                            >
                                {node.label}
                            </th>
                        );
                    } else if (currentLevel < level && node.children && node.children.length > 0) {
                        // Scendi nei figli
                        addCellsForLevel(node.children, currentLevel + 1);
                    }
                });
            };

            addCellsForLevel(colonneStruttura, 0);

            // Colonna Totale (solo nella prima riga)
            if (level === 0) {
                cells.push(
                    <th key="totale" colSpan={metriche.length} rowSpan={maxDepth} className="th-totale">
                        Totale
                    </th>
                );
            }

            headerRows.push(<tr key={`header-${level}`}>{cells}</tr>);
        }

        // Ultima riga: intestazioni metriche
        const metricheRow = [];
        flatPeriodi.forEach((periodo, pidx) => {
            metriche.forEach((m, midx) => {
                metricheRow.push(
                    <th key={`${periodo.key}-${midx}`} className="th-metrica">
                        {m.label}
                    </th>
                );
            });
        });
        // Metriche totale
        metriche.forEach((m, midx) => {
            metricheRow.push(
                <th key={`tot-${midx}`} className="th-metrica th-totale-metrica">
                    {m.label}
                </th>
            );
        });
        headerRows.push(<tr key="header-metriche">{metricheRow}</tr>);

        return headerRows;
    }, [tableData, metriche, selectedRighe, getMaxDepth, getLeafCount]);

    /**
     * Renderizza ricorsivamente le righe della tabella
     */
    const renderHierarchyRows = useCallback(() => {
        const { hierarchy, flatPeriodi, sortedRighe } = tableData;
        if (!hierarchy || Object.keys(hierarchy).length === 0) return null;

        const rows = [];

        const renderNode = (nodeId, node, level, parentKey) => {
            const rowKey = parentKey ? `${parentKey}|${nodeId}` : nodeId;
            const nodeExpanded = isExpanded(rowKey);
            const hasChildren = node.children && Object.keys(node.children).length > 0;

            // Label della dimensione
            const dimension = node.dimension || (sortedRighe[level - 1]?.value);
            const displayLabel = getDimensionLabel(dimension, nodeId);

            rows.push(
                <tr key={rowKey} className={`tr-level-${level}`}>
                    <td
                        className={`td-settore ${hasChildren ? 'td-expandable' : ''} td-indent-${level - 1}`}
                        onClick={() => hasChildren && toggleRow(rowKey)}
                    >
                        <span className="expand-icon">{hasChildren ? (nodeExpanded ? '▼' : '▶') : '•'}</span>
                        <span className="level-value" title={displayLabel}>{displayLabel}</span>
                    </td>
                    {flatPeriodi.map((periodo) => {
                        const data = node.metriche?.[periodo.key] || {};
                        return metriche.map((m, midx) => {
                            const val = data[m.key] || 0;
                            const cellClass = m.key.includes('Straordinari')
                                ? getCellClass('straordinari', val)
                                : m.key === 'oreNonLavoratePerc'
                                    ? getCellClass('percNonLav', val)
                                    : '';
                            return (
                                <td key={`${rowKey}-${periodo.key}-${midx}`} className={cellClass}>
                                    {m.isPerc ? `${formatNumber(val)}%` : formatNumber(val)}
                                </td>
                            );
                        });
                    })}
                    {/* Totale */}
                    {metriche.map((m, midx) => {
                        const val = node.totale?.[m.key] || 0;
                        const cellClass = m.key.includes('Straordinari')
                            ? getCellClass('straordinari', val)
                            : m.key === 'oreNonLavoratePerc'
                                ? getCellClass('percNonLav', val)
                                : '';
                        return (
                            <td key={`tot-${rowKey}-${midx}`} className={`td-totale ${cellClass}`}>
                                {level === 1 ? <strong>{m.isPerc ? `${formatNumber(val)}%` : formatNumber(val)}</strong> : (m.isPerc ? `${formatNumber(val)}%` : formatNumber(val))}
                            </td>
                        );
                    })}
                </tr>
            );

            // Render figli se espanso
            if (nodeExpanded && hasChildren) {
                Object.entries(node.children)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .forEach(([childId, childNode]) => {
                        renderNode(childId, childNode, level + 1, rowKey);
                    });
            }
        };

        // Render livello top
        Object.entries(hierarchy)
            .sort(([a], [b]) => a.localeCompare(b))
            .forEach(([nodeId, node]) => {
                renderNode(nodeId, node, 1, '');
            });

        return rows;
    }, [tableData, metriche, isExpanded, toggleRow, getDimensionLabel, getCellClass, formatNumber]);

    return (
        <div className="dettaglio-comm-container">
            {isDataReady ? (
                <div className="dettaglio-comm-content">
                    {/* KPI Summary Cards */}
                    <div className="oper-hr-kpi-cards dettaglio-comm-kpi">
                        <div className="oper-hr-kpi-card">
                            <div className="oper-hr-kpi-icon operatori">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                            </div>
                            <div className="oper-hr-kpi-content">
                                <span className="oper-hr-kpi-label">Operatori</span>
                                <span className="oper-hr-kpi-value">
                                    <AnimatedCounter value={kpiSummary.numOperatori} />
                                </span>
                                <span className="oper-hr-kpi-sublabel">
                                    Media <AnimatedCounter value={kpiSummary.mediaOreOperatore} decimals={1} suffix=" h/op" />
                                </span>
                            </div>
                        </div>

                        <div className="oper-hr-kpi-card">
                            <div className="oper-hr-kpi-icon commesse">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                                </svg>
                            </div>
                            <div className="oper-hr-kpi-content">
                                <span className="oper-hr-kpi-label">Commesse Attive</span>
                                <span className="oper-hr-kpi-value">
                                    <AnimatedCounter value={kpiSummary.numCommesse} />
                                </span>
                                <span className="oper-hr-kpi-sublabel">Nel periodo selezionato</span>
                            </div>
                        </div>

                        <div className="oper-hr-kpi-card">
                            <div className="oper-hr-kpi-icon lavorate">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                                </svg>
                            </div>
                            <div className="oper-hr-kpi-content">
                                <span className="oper-hr-kpi-label">Ore Lavorate</span>
                                <span className="oper-hr-kpi-value">
                                    <AnimatedCounter value={kpiSummary.oreLavorate} decimals={1} suffix=" h" />
                                </span>
                                <span className="oper-hr-kpi-sublabel">
                                    {kpiSummary.oreDaContratto > 0
                                        ? <><AnimatedCounter value={(kpiSummary.oreLavorate / kpiSummary.oreDaContratto) * 100} decimals={1} suffix="%" /> del contratto</>
                                        : 'N/D'
                                    }
                                </span>
                            </div>
                        </div>

                        <div className="oper-hr-kpi-card oper-hr-kpi-card-with-ring">
                            <ProgressRing
                                value={kpiSummary.percAssenteismo}
                                size={56}
                                strokeWidth={5}
                                thresholds={{ warning: 5, danger: 15 }}
                                inverse={true}
                                label="Assenteismo"
                            />
                            <div className="oper-hr-kpi-content">
                                <span className="oper-hr-kpi-label">Ore Non Lavorate</span>
                                <span className="oper-hr-kpi-sublabel">
                                    <AnimatedCounter value={kpiSummary.oreNonLavorate} decimals={1} suffix=" h" />
                                </span>
                            </div>
                        </div>

                        <div className="oper-hr-kpi-card">
                            <div className="oper-hr-kpi-icon straordinarie-pos">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
                                </svg>
                            </div>
                            <div className="oper-hr-kpi-content">
                                <span className="oper-hr-kpi-label">Ore Straordinarie</span>
                                <span className="oper-hr-kpi-value straordinari">
                                    <AnimatedCounter
                                        value={kpiSummary.oreStraordinarie}
                                        decimals={1}
                                        prefix={kpiSummary.oreStraordinarie > 0 ? '+' : ''}
                                        suffix=" h"
                                    />
                                </span>
                                <span className="oper-hr-kpi-sublabel">
                                    {kpiSummary.oreDaContratto > 0
                                        ? <><AnimatedCounter
                                                value={(kpiSummary.oreStraordinarie / kpiSummary.oreDaContratto) * 100}
                                                decimals={1}
                                                prefix={kpiSummary.oreStraordinarie > 0 ? '+' : ''}
                                                suffix="%"
                                            /> vs contratto</>
                                        : 'N/D'
                                    }
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Parte Sinistra - Tabella */}
                    <div className={`dettaglio-comm-table-section${isFullscreenTable ? ' fullscreen-dettaglio-comm' : ''}`} ref={tableSectionRef}>
                        <RotateDevice active={isFullscreenTable} />
                        <div className="dettaglio-comm-table-header">
                            <span className="dettaglio-comm-table-title">Dettaglio Ore Per Attività</span>
                            <div className="dettaglio-comm-table-actions">
                                <button
                                    className="dettaglio-comm-download-btn"
                                    onClick={() => handleDownloadFile(prepareExportData())}
                                    disabled={dataArrayForCheck.length === 0}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7 10 12 15 17 10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    Esporta
                                </button>
                                <button
                                    className="dettaglio-comm-fullscreen-btn"
                                    onClick={() => setIsFullscreenTable(!isFullscreenTable)}
                                    title={isFullscreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenTable ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Riga configurazione con i 3 dropdown */}
                        <div className="dettaglio-comm-config-row">
                            <div className="dettaglio-comm-config-item">
                                <label>Colonne</label>
                                <Select
                                    classNamePrefix="dettaglio-comm-select"
                                    placeholder="Seleziona..."
                                    value={selectedColonne}
                                    options={COLONNE_OPTIONS}
                                    onChange={(val) => setSelectedColonne(val && val.length > 0 ? val : [COLONNE_OPTIONS[2]])}
                                    isMulti
                                    components={{ MultiValue: MultiValueContainer }}
                                    hideSelectedOptions={false}
                                    closeMenuOnSelect={false}
                                />
                            </div>
                            <div className="dettaglio-comm-config-item">
                                <label>Righe</label>
                                <Select
                                    classNamePrefix="dettaglio-comm-select"
                                    placeholder="Seleziona..."
                                    value={selectedRighe}
                                    options={RIGHE_OPTIONS}
                                    onChange={(val) => setSelectedRighe(val && val.length > 0 ? val : [RIGHE_OPTIONS[0]])}
                                    isMulti
                                    components={{ MultiValue: MultiValueContainer }}
                                    hideSelectedOptions={false}
                                    closeMenuOnSelect={false}
                                />
                            </div>
                            <div className="dettaglio-comm-config-item">
                                <label>Indicatori</label>
                                <Select
                                    classNamePrefix="dettaglio-comm-select"
                                    placeholder="Seleziona..."
                                    value={selectedIndicatori}
                                    options={INDICATORI_OPTIONS}
                                    onChange={(val) => setSelectedIndicatori(val && val.length > 0 ? val : [INDICATORI_OPTIONS[0]])}
                                    isMulti
                                    components={{ MultiValue: MultiValueContainer }}
                                    hideSelectedOptions={false}
                                    closeMenuOnSelect={false}
                                />
                            </div>
                        </div>

                        <div className="dettaglio-comm-table-wrapper">
                            {filteredHrData.length === 0 ? (
                                <div className="dettaglio-comm-no-data">
                                    <span>Nessun dato trovato per i filtri selezionati</span>
                                </div>
                            ) : (
                            <table className="dettaglio-comm-table">
                                <thead>
                                    {renderHeaderRows()}
                                </thead>
                                <tbody>
                                    {/* Rendering gerarchico dinamico */}
                                    {renderHierarchyRows()}

                                    {/* Riga totali */}
                                    <tr className="tr-totali">
                                        <td className="td-settore"><strong>TOTALE</strong></td>
                                        {tableData.flatPeriodi?.map((periodo) => {
                                            const totPeriodo = tableData.totaleGenerale?.metriche?.[periodo.key] || {};
                                            return metriche.map((m, midx) => {
                                                const val = totPeriodo[m.key] || 0;
                                                const cellClass = m.key.includes('Straordinari')
                                                    ? getCellClass('straordinari', val)
                                                    : m.key === 'oreNonLavoratePerc'
                                                        ? getCellClass('percNonLav', val)
                                                        : '';
                                                return (
                                                    <td key={`totrow-${periodo.key}-${midx}`} className={cellClass}>
                                                        <strong>{m.isPerc ? `${formatNumber(val)}%` : formatNumber(val)}</strong>
                                                    </td>
                                                );
                                            });
                                        })}
                                        {/* Totale generale */}
                                        {metriche.map((m, midx) => {
                                            const val = tableData.totaleGenerale?.totale?.[m.key] || 0;
                                            const cellClass = m.key.includes('Straordinari')
                                                ? getCellClass('straordinari', val)
                                                : m.key === 'oreNonLavoratePerc'
                                                    ? getCellClass('percNonLav', val)
                                                    : '';
                                            return (
                                                <td key={`grandtot-${midx}`} className={`td-totale ${cellClass}`}>
                                                    <strong>{m.isPerc ? `${formatNumber(val)}%` : formatNumber(val)}</strong>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </tbody>
                            </table>
                            )}
                        </div>
                    </div>

                    {/* Parte Destra - Grafico */}
                    <div className={`dettaglio-comm-chart-section${isFullscreenChart ? ' fullscreen-dettaglio-comm' : ''}`} ref={chartSectionRef}>
                        <RotateDevice active={isFullscreenChart} />
                        <div className="dettaglio-comm-chart-header">
                            <span className="dettaglio-comm-chart-title">Top Commesse Per Straordinari</span>
                            <div className="dettaglio-comm-chart-actions">
                                {selectedCommesse.length > 0 && (
                                    <button
                                        className="dettaglio-comm-clear-filter-btn"
                                        onClick={handleClearSelection}
                                        title="Rimuovi filtro"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                        </svg>
                                        Rimuovi filtro ({selectedCommesse.length})
                                    </button>
                                )}
                                <button
                                    className="dettaglio-comm-download-btn"
                                    onClick={handleDownloadChartCommesse}
                                    disabled={chartData.commesse.length === 0}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7 10 12 15 17 10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    Esporta
                                </button>
                                <button
                                    className="dettaglio-comm-fullscreen-btn"
                                    onClick={() => setIsFullscreenChart(!isFullscreenChart)}
                                    title={isFullscreenChart ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenChart ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Badge filtro attivo */}
                        {selectedCommesse.length > 0 && (
                            <div className="dettaglio-comm-filter-badge">
                                <span className="dettaglio-comm-filter-label">Filtrando per:</span>
                                <div className="dettaglio-comm-filter-tags">
                                    {selectedCommesse.map(commessaId => {
                                        const label = commesseIdToLabel.get(commessaId) || commessaId;
                                        return (
                                            <span key={commessaId} className="dettaglio-comm-filter-tag" title={label}>
                                                <span className="dettaglio-comm-filter-tag-text">{label}</span>
                                                <button
                                                    onClick={() => setSelectedCommesse(prev => prev.filter(id => id !== commessaId))}
                                                    title="Rimuovi"
                                                >
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                                    </svg>
                                                </button>
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {chartData.commesse.length > 0 ? (
                            <ReactEChartsCore
                                ref={chartRef}
                                echarts={echarts}
                                option={getChartOptions()}
                                style={{ height: '100%', minHeight: '400px' }}
                                onEvents={{
                                    click: handleChartClick
                                }}
                            />
                        ) : (
                            <div className="dettaglio-comm-empty-chart">
                                <div className="dettaglio-comm-empty-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M3 3v18h18"></path>
                                        <path d="M18 9l-5 5-4-4-3 3"></path>
                                    </svg>
                                </div>
                                <p>Nessuna commessa con ore straordinarie</p>
                            </div>
                        )}
                    </div>
                </div>
            ) : isLoading ? (
                <Lf23Loader />
            ) : isEmpty ? (
                <div className="dettaglio-comm-empty-state">
                    <div className="dettaglio-comm-empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                        </svg>
                    </div>
                    <h4>Nessun dato disponibile</h4>
                    <p>Non ci sono dati per i filtri selezionati. Prova a modificare i filtri.</p>
                </div>
            ) : (
                <Lf23Loader />
            )}
        </div>
    );
};

export default DettaglioComm;
